import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../i18n/LanguageContext';
import { Helmet } from 'react-helmet';
import Footer from '../../components/layout/Footer';
import { motion } from 'framer-motion';
import Button from '../../components/ui/Button';

// Background image for the page
const backgroundImage = 'https://images.unsplash.com/photo-1556742502-ec7c0e9f34b1?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1170&q=80';

const PageContainer = styled(motion.div)`
  background-color: #000;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const HeroSection = styled(motion.div)`
  background-color: #000;
  height: 20vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: white;
  position: relative;
  text-align: center;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.4);
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1200px;
  width: 100%;
  padding: 0 2rem;
`;

const BackLink = styled(Link)`
  position: absolute;
  top: 2rem;
  left: 2rem;
  color: white;
  text-decoration: none;
  display: flex;
  align-items: center;
  font-size: 1rem;
  z-index: 10;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateX(-5px);
  }
  
  &::before {
    content: '←';
    margin-right: 8px;
    font-size: 1.2rem;
  }
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  font-style: italic;
  text-align: center;
  color: #0d9488;
`;

const Description = styled.p`
  font-size: 1.2rem;
  line-height: 1.7;
  margin-bottom: 2rem;
  text-align: center;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
  color: #e0e0e0;
`;

const ContentSection = styled.div`
  flex: 1;
  width: 100%;
  padding: 4rem 0;
  background-image: linear-gradient(rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.8)), url(${backgroundImage});
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  color: #fff;
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 2.2rem;
  margin: 4rem 0 1rem;
  text-align: center;
  color: #0d9488;
  font-style: italic;
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ServiceCard = styled.div`
  background-color: rgba(13, 148, 136, 0.1);
  border: 1px solid #0d9488;
  border-radius: 8px;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
  }
`;

const ServiceTitle = styled.h3`
  font-size: 1.8rem;
  color: #0d9488;
  margin-bottom: 1rem;
  font-style: italic;
`;

const ServiceDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  color: #e0e0e0;
  margin-bottom: 1.5rem;
  flex: 1;
`;

const CTASection = styled.div`
  margin-top: 6rem;
  text-align: center;
`;

const Card: React.FC = () => {
  const { language } = useLanguage();
  
  // Animation variants
  const pageVariants = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 }
  };

  const heroVariants = {
    initial: { y: -50, opacity: 0 },
    animate: { y: 0, opacity: 1, transition: { duration: 0.6 } }
  };
  
  // Services data
  const cards = [
    {
      title: language === 'it' ? 'GIFT CARD' : 'GIFT CARD',
      description: language === 'it' 
        ? 'La gift card, o carta regalo, è una forma di pagamento prepagato emessa da J-O-H, che può essere utilizzata per effettuare acquisti presso il negozio o servizio specificato sulla carta. Le gift card sono un modo per dare flessibilità nella scelta, permettendo al destinatario di selezionare i prodotti o i servizi che desidera.'
        : 'The gift card is a form of prepaid payment issued by J-O-H, which can be used to make purchases at the store or service specified on the card. Gift cards are a way to give flexibility in choice, allowing the recipient to select the products or services they want.',
      path: '/services/gift-card'
    },
    {
      title: language === 'it' ? 'FIDATY CARD' : 'LOYALTY CARD',
      description: language === 'it' 
        ? 'La carta fiducia, o carta fedeltà, è una carta collegata a un programma di fidelizzazione, in cui i clienti accumulano punti o ricevono sconti in cambio di acquisti ripetuti o di una certa quantità di spesa. I clienti possono accumulare punti o ottenere sconti speciali utilizzando la carta per gli acquisti o partecipando ad attività specifiche.'
        : 'The trust card, or loyalty card, is a card linked to a loyalty program, in which customers accumulate points or receive discounts in exchange for repeated purchases or a certain amount of spending. Customers can accumulate points or get special discounts by using the card for purchases or participating in specific activities.',
      path: '/services/fidaty-card'
    }
  ];
  
  return (
    <PageContainer
      initial="initial"
      animate="animate"
      exit="exit"
      variants={pageVariants}
    >
      <Helmet>
        <title>{language === 'it' ? 'Card | J-O-H' : 'Cards | J-O-H'}</title>
        <meta 
          name="description" 
          content={language === 'it' 
            ? 'Scopri le nostre carte regalo e fedeltà per accedere a servizi esclusivi e vantaggi speciali' 
            : 'Discover our gift and loyalty cards to access exclusive services and special benefits'} 
        />
      </Helmet>
      
      <HeroSection variants={heroVariants}>
        <BackLink to="/services/property-tenant">
          {language === 'it' ? 'Indietro' : 'Back'}
        </BackLink>
        <HeroContent>
          <PageTitle>CARD</PageTitle>
        </HeroContent>
      </HeroSection>
      
      <ContentSection>
        <ContentContainer>
          <Description>
            {language === 'it' 
              ? 'Scopri le nostre carte speciali che ti permettono di accedere a servizi esclusivi e vantaggi dedicati. Che tu stia cercando un regalo perfetto o desideri premiare la tua fedeltà, abbiamo la soluzione ideale per te.'
              : 'Discover our special cards that allow you to access exclusive services and dedicated benefits. Whether you are looking for a perfect gift or want to reward your loyalty, we have the ideal solution for you.'}
          </Description>
          
          <ServicesGrid>
            {cards.map((card, index) => (
              <ServiceCard key={index}>
                <ServiceTitle>{card.title}</ServiceTitle>
                <ServiceDescription>{card.description}</ServiceDescription>
                <Button variant="outline" to={card.path}>
                  {language === 'it' ? 'Scopri' : 'Discover'}
                </Button>
              </ServiceCard>
            ))}
          </ServicesGrid>
          
          <CTASection>
            <Button variant="primary" to="/contact">
              {language === 'it' ? 'Contattaci per maggiori informazioni' : 'Contact us for more information'}
            </Button>
          </CTASection>
        </ContentContainer>
      </ContentSection>
      
      <Footer />
    </PageContainer>
  );
};

export default Card; 